/** Owns Trade Products preference loading and ordered persistence into the shared defaults API. */
import {configureStore, createAsyncThunk, createReducer} from '@reduxjs/toolkit';
import {getQepSystem, getUserAccountsFromTraderDB} from '@qep/shared/src';
import {getAppEnv} from '@qep/shared/src/utils/runtime-config';
import {
  type DefaultUpdateResponse, type ProductQtyDefault, type TradableSymbol, type TraderDBAccount,
} from '@qep/types/src';

/** Describes one field-level product change without capturing unrelated, potentially stale fields. */
export type ProductDefaultChange =
  | {kind: 'account'; product: string; value: string}
  | {kind: 'displayQuantity'; product: string; value: number}
  | {kind: 'iceberg'; product: string; value: boolean}
  | {kind: 'quantity'; product: string; value: number};

export type DefaultPreferencesState = {
  fetchedProducts: boolean;
  fetchedAccounts: boolean;
  accounts: TraderDBAccount[];
  products: TradableSymbol[];
};

const defaultDefaultPreferencesState: DefaultPreferencesState = {
  fetchedAccounts: false,
  fetchedProducts: false,
  accounts: [],
  products: [],
};

const initialState: DefaultPreferencesState = defaultDefaultPreferencesState;

export const getDefaults = createAsyncThunk('getDefaults', async (): Promise<TradableSymbol[]> => getQepSystem().apiServices.defaults.getDefaults());

export const getAccounts = createAsyncThunk('getAccounts', async () => {
  const accounts: TraderDBAccount[] = await getUserAccountsFromTraderDB();
  return accounts;
});

const persistDefaults = createAsyncThunk('setDefaults', async (products: ProductQtyDefault[]): Promise<DefaultUpdateResponse> => {
  const qepSystem = getQepSystem();

  if (!qepSystem?.apiServices?.defaults?.setDefaults) {
    throw new Error('setDefaults API method not available');
  }

  return qepSystem.apiServices.defaults.setDefaults(products);
});

export const defaultsPreferencesReducer = createReducer(initialState, builder => {
  builder
    .addCase(getDefaults.fulfilled, (state, {payload}) => {
      state.products = payload;
      state.fetchedProducts = true;
    })
    .addCase(getDefaults.rejected, state => {
      state.fetchedProducts = true;
    })
    .addCase(getAccounts.fulfilled, (state, {payload}) => {
      state.accounts = payload;
      state.fetchedAccounts = true;
    })
    .addCase(getAccounts.rejected, state => {
      state.fetchedAccounts = true;
    })
    .addCase(persistDefaults.fulfilled, (state, {payload}) => {
      if (payload.products) {
        state.products = payload.products;
      }
    });
});

const reducer = {
  defaultsPreferencesReducer,
};

const isDevToolsEnabled = getAppEnv() !== 'production' && getAppEnv() !== 'preprod';

/** Creates an isolated Trade Products store for the application or deterministic integration tests. */
export function createTradeProductsStore() {
  return configureStore({
    reducer,
    middleware: getDefaultMiddleware => getDefaultMiddleware({serializableCheck: false}),
    devTools: isDevToolsEnabled,
    preloadedState: {},
  });
}

// eslint-disable-next-line unicorn/prevent-abbreviations
export const applicationStore = createTradeProductsStore();

export type AppDispatch = typeof applicationStore.dispatch;
type AppGetState = typeof applicationStore.getState;

/** Returns the stable API product key used to locate one authoritative product. */
function getProductKey(product: TradableSymbol): string {
  return `${product.rootSymbol}\\${product.segmentMic}`;
}

/** Normalizes optional product fields into the complete defaults persistence payload. */
function buildProductDefault(product: TradableSymbol): ProductQtyDefault {
  return {
    accountId: product.defaultAccount ?? '',
    icebergDisplayQty: typeof product.icebergDisplayQty === 'number' && Number.isFinite(product.icebergDisplayQty) ? product.icebergDisplayQty : 0,
    isAcctSetByUser: product.acctSetByUser ?? false,
    isIceberg: product.isIceberg ?? false,
    isQtySetByUser: product.qtySetByUser ?? false,
    product: getProductKey(product),
    qty: product.defaultQty ?? 0,
  };
}

/** Applies one UI change intent to the latest product without replacing unrelated defaults fields. */
function applyProductDefaultChange(product: TradableSymbol, change: ProductDefaultChange): ProductQtyDefault {
  const productDefault = buildProductDefault(product);

  switch (change.kind) {
    case 'account': {
      return {...productDefault, accountId: change.value, isAcctSetByUser: true};
    }

    case 'displayQuantity': {
      return {...productDefault, icebergDisplayQty: change.value};
    }

    case 'iceberg': {
      return {
        ...productDefault,
        icebergDisplayQty: change.value ? productDefault.icebergDisplayQty : 0,
        isIceberg: change.value,
      };
    }

    case 'quantity': {
      return {...productDefault, isQtySetByUser: true, qty: change.value};
    }
  }
}

/**
 * Creates an application-lifetime FIFO updater for full-list defaults responses.
 * Each operation reads Redux only when it reaches the queue head, commits the API response,
 * and completes its broadcast before the next operation starts. A finally-released queue gate
 * lets later edits proceed even when the current operation rejects.
 */
export function createProductDefaultUpdater() {
  let queueTail: Promise<void> = Promise.resolve();

  /** Queues one field-level change and resolves with its authoritative defaults response. */
  return (change: ProductDefaultChange) => async (dispatch: AppDispatch, getState: AppGetState): Promise<DefaultUpdateResponse> => {
    const previousOperation = queueTail;
    let releaseQueue!: () => void;
    queueTail = new Promise<void>(resolve => {
      releaseQueue = resolve;
    });

    await previousOperation;
    try {
      const product = getState().defaultsPreferencesReducer.products.find(candidate => getProductKey(candidate) === change.product);
      if (!product) {
        throw new Error(`Cannot update defaults for unavailable product ${change.product}`);
      }

      const response = await dispatch(persistDefaults([applyProductDefaultChange(product, change)])).unwrap();
      await getQepSystem().container.applicationClient.broadcast('defaults_updated', response.products);
      return response;
    } finally {
      releaseQueue();
    }
  };
}

export const updateProductDefault = createProductDefaultUpdater();
